import dayjs from 'dayjs';

import {
  getDecimalNumbersOnly,
  getFileNameFromUrl,
  mapOrDefault,
  sortByKey
} from '@utils/common';
import { getFormattedAmount, getNumberFromString } from '@utils/amount';

import {
  CreatePreIPORequest,
  PreIPODealFormValues,
  PreIPODeals,
  PreIPOTerm,
  UpdatePreIPORequest
} from './types';
import { preIPODealFormDefaultValues } from './constants';

export const formatGetPreIPOApi = (
  preIPOResponse: PreIPODeals,
  passId: boolean = false
) => {
  const getPreIPODocuments = () =>
    mapOrDefault(
      preIPOResponse.documents,
      document => ({
        id: passId ? document.id : '',
        title: document.name,
        description: document.description,
        document: {
          file: document.file_path ? new File([], '') : undefined,
          path: document.file_path || ''
        }
      }),
      preIPODealFormDefaultValues.preIPODocuments
    );

  const getFaqs = () =>
    mapOrDefault(
      preIPOResponse.faq_sections,
      faqs => ({
        id: passId ? faqs.id : '',
        title: faqs.title,
        shouldDisplay: faqs.is_visible,
        questions: mapOrDefault(
          faqs.faqs,
          faq => ({
            id: passId ? faq.id : '',
            question: faq.question,
            answer: faq.answer
          }),
          []
        )
      }),
      preIPODealFormDefaultValues.faq
    );

  const getNews = () => {
    const news = preIPOResponse.news || [];
    const sortedNews = sortByKey(news, 'order');
    const hasDuplicateOrders =
      new Set(sortedNews.map(item => item.order)).size !== sortedNews.length;

    return mapOrDefault(
      sortedNews,
      (item, idx) => ({
        id: item.id,
        url: item.url,
        order: hasDuplicateOrders ? idx : item.order ?? idx
      }),
      preIPODealFormDefaultValues.news
    );
  };

  const getPreIPOHighlights = () => {
    const highlights = preIPOResponse.preipo_highlights || [];
    const sortedHighlights = sortByKey(highlights, 'sequence');
    const hasDuplicateSequences =
      new Set(sortedHighlights.map(item => item.sequence)).size !==
      sortedHighlights.length;

    return mapOrDefault(
      sortedHighlights,
      (highlight, idx) => ({
        id: passId ? highlight.id : '',
        title: highlight.title,
        description: highlight.deal_highlight,
        sequence: hasDuplicateSequences ? idx : highlight.sequence
      }),
      preIPODealFormDefaultValues.preIPOHighlights
    );
  };

  const getQuickLinks = () =>
    mapOrDefault(
      preIPOResponse.quick_link,
      item => ({
        id: item.id,
        url: item.url,
        quickLinkEntryId: item.quick_link_entry_id
      }),
      preIPODealFormDefaultValues.quickLinks
    );

  const getContents = () =>
    mapOrDefault(
      preIPOResponse.content,
      item => ({
        id: item.id,
        title: item.title,
        url: item.url,
        description: item.description
      }),
      preIPODealFormDefaultValues.preIPOContent
    );
  const getTermValue = (terms: PreIPOTerm[], key: string) =>
    terms.find(term => term.term_key === key);

  const terms = preIPOResponse.preipo_terms || [];

  const minInvest = getTermValue(terms, 'MIN. INVEST');
  const maxInvest = getTermValue(terms, 'MAX. INVEST');
  const settlementTime = getTermValue(terms, 'SETTLEMENT TIME');
  const paymentDate = getTermValue(terms, 'PAYMENT DATE');
  const lastDaySharePrice = getTermValue(terms, 'LAST DAY SHARE PRICE');
  const expectedIPODate = getTermValue(terms, 'EXPECTED IPO DATE');
  const isin = getTermValue(terms, 'ISIN');

  return {
    id: passId ? preIPOResponse.id : '',
    isPublish: false,
    isDraft: false,
    companyId: preIPOResponse.company_id,
    status: preIPOResponse.status,

    //Tab 1: Pre-IPO Details
    preIPOType: preIPOResponse.type,
    preIPOMode: preIPOResponse.mode,
    isin: preIPOResponse.isin,

    preIPOName: preIPOResponse.name,
    startDate: dayjs(preIPOResponse.start_time).format(
      'YYYY-MM-DD HH:mm:ss'
    ) as string,
    endDate: dayjs(preIPOResponse.expiry_time).format(
      'YYYY-MM-DD HH:mm:ss'
    ) as string,

    minInvestment: getFormattedAmount(preIPOResponse.min_investment || ''),
    maxInvestment: getFormattedAmount(preIPOResponse.max_investment || ''),

    minInvestPreIPOTerm: minInvest?.term_value ?? '',
    maxInvestPreIPOTerm: maxInvest?.term_value ?? '',
    settlementTime: settlementTime?.term_value ?? '',
    paymentDate: paymentDate?.term_value ?? '',
    lastDaySharePrice: lastDaySharePrice?.term_value ?? '',
    expectedIPODate: expectedIPODate?.term_value ?? '',
    isinTerms: isin?.term_value ?? '',

    minInvestPreIPOTermId: minInvest?.id ?? '',
    maxInvestPreIPOTermId: maxInvest?.id ?? '',
    settlementTimeId: settlementTime?.id ?? '',
    paymentDateId: paymentDate?.id ?? '',
    lastDaySharePriceId: lastDaySharePrice?.id ?? '',
    expectedIPODateId: expectedIPODate?.id ?? '',
    isinId: isin?.id ?? '',

    preIPOHighlights: getPreIPOHighlights(),

    coverImage: {
      file: preIPOResponse.cover_image
        ? new File([], getFileNameFromUrl(preIPOResponse.cover_image))
        : undefined,
      path: preIPOResponse.cover_image || ''
    },
    backgroundImage: {
      file: preIPOResponse.background_image
        ? new File([], getFileNameFromUrl(preIPOResponse.background_image))
        : undefined,
      path: preIPOResponse.background_image || ''
    },

    preIPODocuments: getPreIPODocuments(),

    faq: getFaqs(),
    news: getNews(),
    quickLinks: getQuickLinks(),
    versionNumber: preIPOResponse.version,
    isSharePriceAvailable: false,
    soldOut: preIPOResponse.configuration.is_sold_out,
    displayNewTag: preIPOResponse.configuration.is_new,
    preIPOContent: getContents()
  };
};

const setPreIPOTerms = (data: PreIPODealFormValues, includeIds = false) => {
  const terms = [
    {
      id: data.minInvestPreIPOTermId,
      term_key: 'MIN. INVEST',
      term_value: data.minInvestPreIPOTerm,
      sequence: 0
    },
    {
      id: data.maxInvestPreIPOTermId,
      term_key: 'MAX. INVEST',
      term_value: data.maxInvestPreIPOTerm,
      sequence: 1
    },
    {
      id: data.settlementTimeId,
      term_key: 'SETTLEMENT TIME',
      term_value: data.settlementTime,
      sequence: 2
    },
    {
      id: data.paymentDateId,
      term_key: 'PAYMENT DATE',
      term_value: data.paymentDate,
      sequence: 3
    },
    {
      id: data.expectedIPODateId,
      term_key: 'EXPECTED IPO DATE',
      term_value: data.expectedIPODate,
      sequence: 4
    },
    {
      id: data.isinId,
      term_key: 'ISIN',
      term_value: data.isinTerms,
      sequence: 5
    }
  ];

  const filteredTerms = terms
    .filter(term => term.term_value?.trim())
    .map(term =>
      includeIds
        ? term
        : {
            term_key: term.term_key,
            term_value: term.term_value,
            sequence: term.sequence
          }
    );

  return filteredTerms.length > 0 ? filteredTerms : undefined;
};

const setHighlights = (data: PreIPODealFormValues) => {
  const highlights = data.preIPOHighlights
    .filter(item => !!item.description)
    .map(item => ({
      id: item.id || '',
      preipo_highlight: item.description?.trim(),
      title: item.title?.trim(),
      sequence: item.sequence
    }));
  return highlights.length > 0 ? highlights : undefined;
};

const setDocuments = (data: PreIPODealFormValues) => {
  const documents = data.preIPODocuments
    .filter(item => !!item.title)
    .map(item => ({
      id: item.id || '',
      name: item.title?.trim(),
      description: item.description?.trim(),
      file_path: item.document.path.length > 1 ? item.document.path : undefined
    }));
  return documents.length > 0 ? documents : undefined;
};
const setContent = (data: PreIPODealFormValues) => {
  const contents = data.preIPOContent
    .filter(item => !!item.title)
    .map(item => ({
      id: item.id,
      title: item.title?.trim(),
      url: item.url,
      description: item.description?.trim()
    }));
  return contents.length > 0 ? contents : undefined;
};

const setFaqs = (data: PreIPODealFormValues) => {
  const contents = data.faq
    .filter(item => !!item.title)
    .map(item => ({
      faq_section: { id: item.id, title: item.title?.trim(), description: '' },
      faqs: item.questions
        .filter(faq => faq.question?.trim() || faq.answer?.trim())
        .map(faq => ({
          id: faq.id,
          question: faq.question?.trim(),
          answer: faq.answer?.trim()
        })),
      is_visible: item.shouldDisplay
    }));
  return contents.length > 0 ? contents : undefined;
};

const setNews = (data: PreIPODealFormValues) => {
  const news = data.news
    .filter(item => !!item.url)
    .map(item => ({ id: item.id, url: item.url, order: item.order }));
  return news.length > 0 ? news : undefined;
};

const setQuickLinks = (data: PreIPODealFormValues) => {
  const quickLinks = data.quickLinks
    .filter(item => !!item.url)
    .map(item => ({
      id: item.id,
      url: item.url,
      quick_link_entry_id: item.quickLinkEntryId
    }));
  return quickLinks.length > 0 ? quickLinks : undefined;
};

const setChargesAndFees = (
  data: PreIPODealFormValues,
  setUtcDateWithTimeZone: (date: Date) => string
) => {
  const chargesAndFeesBrackets = data.chargesAndFees.map(item => ({
    bracket_min:
      item.lowerLimit === '0' && item.upperLimit === ''
        ? ''
        : getDecimalNumbersOnly(item.lowerLimit),
    bracket_max: getDecimalNumbersOnly(item.upperLimit),
    document_charges:
      getDecimalNumbersOnly(item.documentationChargesPercent) || '0',
    document_charges_flat:
      getDecimalNumbersOnly(item.documentationChargesAmount) || '0',
    platform_charges: getDecimalNumbersOnly(item.platformChargesPercent) || '0',
    platform_charges_flat:
      getDecimalNumbersOnly(item.platformChargesAmount) || '0',
    tax: getDecimalNumbersOnly(item.tax) || '0',
    carry_short_description: data.shortDescription,
    carry_long_description: data.longDescription,
    type: 'resident-individual',
    stamp_duty: getDecimalNumbersOnly(item.stampDuty) || '0',
    dp_charges: getDecimalNumbersOnly(item.dpCharges) || '0'
  }));

  const hasValidChargesAndFees = chargesAndFeesBrackets.some(item =>
    Object.values(item).some(value => value !== '')
  );

  if (!hasValidChargesAndFees) {
    return null;
  }

  return {
    charges_and_fees_brackets: chargesAndFeesBrackets,
    visibility: data.sharePriceVisibility,
    date: data.sharePriceDate
      ? setUtcDateWithTimeZone(new Date(data.sharePriceDate))
      : ''
  };
};

const setSharePrice = (
  data: PreIPODealFormValues,
  setUtcDateWithTimeZone: (date: Date) => string
) => {
  const chargesAndFees = data.chargesAndFees.map(item => ({
    min_amount:
      item.lowerLimit === '0' && item.upperLimit === ''
        ? ''
        : getDecimalNumbersOnly(item.lowerLimit),
    max_amount: getDecimalNumbersOnly(item.upperLimit),
    share_price: getDecimalNumbersOnly(item.sharePrice)
  }));

  const sharePriceBrackets = chargesAndFees.every(item =>
    Object.values(item).every(value => value === '')
  )
    ? null
    : chargesAndFees;

  return [
    {
      date: data.sharePriceDate
        ? setUtcDateWithTimeZone(new Date(data.sharePriceDate))
        : '',
      visibility: data.sharePriceVisibility,
      share_price_brackets: sharePriceBrackets
    }
  ];
};

const formatPreIPOApi = (
  data: PreIPODealFormValues,
  state: string,
  setUtcDateWithTimeZone: (date: Date) => string,
  setUtcEndDateWithTimeZone: (date: Date) => string,
  isUpdate: boolean = false
): CreatePreIPORequest | UpdatePreIPORequest => {
  const defaultStartDate = data.endDate ? new Date(data.endDate) : new Date();
  if (data.endDate) defaultStartDate.setDate(defaultStartDate.getDate() - 30);

  const defaultEndDate = data.startDate ? new Date(data.startDate) : new Date();
  defaultEndDate.setDate(defaultEndDate.getDate() + 30);

  const baseRequest = {
    status: state,
    version: data.versionNumber,
    company_id: data.companyId,

    //Tab1: Pre-IPO Details
    type: data.preIPOType,
    mode: data.preIPOMode,
    isin: data.isin?.trim() || undefined,

    name: data.preIPOName || undefined,
    start_time: data.startDate
      ? setUtcDateWithTimeZone(new Date(data.startDate))
      : setUtcDateWithTimeZone(defaultStartDate),
    expiry_time: data.endDate
      ? setUtcEndDateWithTimeZone(new Date(data.endDate))
      : setUtcEndDateWithTimeZone(defaultEndDate),

    min_investment: data.minInvestment
      ? parseInt(getNumberFromString(data.minInvestment))
      : undefined,
    max_investment: data.maxInvestment
      ? parseInt(getNumberFromString(data.maxInvestment))
      : undefined,

    preipo_terms: setPreIPOTerms(data, isUpdate),

    preipo_highlights: setHighlights(data),

    cover_image:
      data.coverImage.path.length > 1 ? data.coverImage.path : undefined,
    background_image:
      data.backgroundImage.path.length > 1
        ? data.backgroundImage.path
        : undefined,

    documents: setDocuments(data),
    content: setContent(data),
    faqs: setFaqs(data),
    news: setNews(data),
    quick_link: setQuickLinks(data),
    configuration: { is_sold_out: data.soldOut, is_new: data.displayNewTag }
  };

  if (isUpdate) {
    if (!data.id) {
      throw new Error('ID is required for updating a Pre-IPO');
    }
    return {
      ...baseRequest,
      id: data.id
    } as UpdatePreIPORequest;
  }

  return {
    ...baseRequest,
    share_prices: data.isSharePriceAvailable
      ? setSharePrice(data, setUtcDateWithTimeZone)
      : undefined,
    individual_charges: data.isSharePriceAvailable
      ? [setChargesAndFees(data, setUtcDateWithTimeZone)]
      : undefined
  } as CreatePreIPORequest;
};

export const formatCreatePreIPOApi = (
  data: PreIPODealFormValues,
  state: string,
  setUtcDateWithTimeZone: (date: Date) => string,
  setUtcEndDateWithTimeZone: (date: Date) => string
): CreatePreIPORequest =>
  formatPreIPOApi(
    data,
    state,
    setUtcDateWithTimeZone,
    setUtcEndDateWithTimeZone
  ) as CreatePreIPORequest;

export const formatUpdatePreIPOApi = (
  data: PreIPODealFormValues,
  state: string,
  setUtcDateWithTimeZone: (date: Date) => string,
  setUtcEndDateWithTimeZone: (date: Date) => string
): UpdatePreIPORequest =>
  formatPreIPOApi(
    data,
    state,
    setUtcDateWithTimeZone,
    setUtcEndDateWithTimeZone,
    true
  ) as UpdatePreIPORequest;
